/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import {
  UPLOAD_REGISTER_SINGLE_ASSET,
  UPLOAD_REGISTER_SINGLE_ASSET_DESCRIPTION
} from '@/configs/strings/upload-register-single-asset'
import { useNavigate } from 'react-router-dom'
import { assetsUrls } from '@/configs/urls'
import UploadFile from '@/pages/Assets/pages/UploadRegisterAsset/components/UploadFile'
import { extractMetadata } from '@/services/metadata.service'
import { useAuth0 } from '@auth0/auth0-react'
import FormWrapper from '@/components/forms/FormWrapper'

const UploadRegisterAsset = () => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [fileType, setFileType] = useState('')
  const [alertMessage, setAlertMessage] = useState()
  const navigate = useNavigate()
  const { assetForm, base } = assetsUrls
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const [hasAlert, setHasAlert] = useState(false)
  const handleFileSelected = (file) => {
    if (file) {
      switch (file.type) {
        case 'application/pdf': setFileType('PDF'); break;
        case 'application/epub+zip': setFileType('EPUB'); break;
        case 'text/plain': setFileType('TXT'); break;
        case 'text/xml': setFileType('XML'); break;
        case 'audio/mpeg': setFileType('MP3'); break;
        case 'audio/wav': setFileType('WAV'); break;
        case 'audio/x--m4a': setFileType('M4A'); break;
        case 'application/x-freearc': setFileType('ARC'); break;
        case 'text/html': setFileType('HTML'); break;
        case 'video/x-msvideo': setFileType('AVI'); break;
        case 'video/quicktime': setFileType('MOV'); break;
        case 'image/heic': setFileType('HEIC'); break;
        case 'image/svg+xml': setFileType('SVG'); break;
        case 'text/csv': setFileType('CSV'); break;
        case 'image/jpeg': setFileType('JPG'); break;
        case 'image/png': setFileType('PNG'); break;
        default:
          switch (file.name.split('.').pop()) {
            case 'las': setFileType('LAS'); break;
            case 'laz': setFileType('LAZ'); break;
            case 'e57': setFileType('E57'); break;
            case 'pdc': setFileType('PDC'); break;
            case 'ply': setFileType('PLY'); break;
            case 'rcs': setFileType('RCS'); break;
            case 'rcp': setFileType('RCP'); break;
            case 'warc': setFileType('WARC'); break;
            case 'md': setFileType('MD'); break;
          }
      }
    }
    setSelectedFile(file)
  }
  const handleSetAlertError = (message, resetError) => {
    setHasAlert(!resetError)

    message && setAlertMessage(message)
  }

  const handleSubmit = async (file) => {
    setIsLoading(true)
    setHasAlert(false)
    try {
      const formData = new FormData()
      formData.append('mimeType', file.type)
      formData.append('fileType', fileType)
      formData.append('file', file)

      const res = await extractMetadata(formData, getAccessToken)

      navigate(`/${base}/${assetsUrls.new}/${assetForm}`, {
        state: { selectedFile: file, metadata: res.data }
      })
    } catch (error) {
      const message = error.response?.data?.message

      message && setAlertMessage(message)

      setIsLoading(false)

      setHasAlert(true)
    }
  }

  return (
    <RegularTemplate
      title={UPLOAD_REGISTER_SINGLE_ASSET}
      description={UPLOAD_REGISTER_SINGLE_ASSET_DESCRIPTION}
    >
        <div className="w-full">
          <div className="max-w-2xl">
            <FormWrapper
              hasAlert={hasAlert}
              alertMessage={alertMessage}
            >
              <form onSubmit={(e) => {
                e.preventDefault()
                if (selectedFile) {
                  handleSubmit(selectedFile)
                }
              }} className="w-full">
                <UploadFile
                  onFileSelected={handleFileSelected}
                  isSubmitting={isLoading}
                  setErrorMessage={handleSetAlertError}
                />
              </form>
            </FormWrapper>
          </div>
        </div>
    </RegularTemplate>
  )
}

export default UploadRegisterAsset
